import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAmbassadorsListFromSearch } from "../../../apis/ambassadors.api";
import {
  getInitiatives,
  getInitiativesForDownload,
  deleteInitiative,
} from "../../../apis/initiatives.api";
import { ButtonWithIcon } from "../../../components/buttons";
import { AppAlertBox } from "../../../components/modals/alert-box";
import { AutoSelectbox } from "../../../components/selectbox/autoselect";
import {
  DEFAULT_SELECTED_PAGE,
  DEFAULT_SELECTED_PAGE_SIZE,
} from "../../../constants";
import { ENUM_AUTOSELECT_LIST_TYPES } from "../../../enums";
import { convertToFullName, showNotification } from "../../../services/utils";
import {
  flattenGroupedInitiatives,
  normalizeInitiativesGroupedByAmbassador,
} from "../../ambassadors/ambassador-details/initiatives/types";
import { INITIATIVE_TYPE_OPTIONS } from "../../ambassadors/ambassador-details/initiatives/utils";
import { AddOrUpdateInitiativeDrawer } from "../add-or-update";
import { downloadInitiativesListCsv } from "./download-utils";
import { InitiativesListTable } from "./table";
import { INITIATIVES_GROUP_COLUMNS } from "./table-config";

const DELETE_ALERT_MESSAGE =
  "Are you sure you want to delete this active initiative? This action cannot be undone.";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const VISIBILITY_OPTIONS = [
  { value: "", label: "All" },
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const INITIAL_FILTERS = {
  selectedPage: DEFAULT_SELECTED_PAGE,
  selectedPageSize: DEFAULT_SELECTED_PAGE_SIZE,
  ambassadorId: "",
  type: "",
  status: "",
  visibility: "",
};

const filterSelectSx = {
  bgcolor: "#FFFFFF",
  height: 40,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.23)",
  },
};

function InitiativesList() {
  const dispatch = useDispatch();
  const [list, setList] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [draftFilters, setDraftFilters] = useState({
    ambassadorId: "",
    type: "",
    status: "",
    visibility: "",
  });
  const [ambassadorList, setAmbassadorList] = useState([]);
  const [selectedAmbassador, setSelectedAmbassador] = useState(null);
  const [ambassadorSelectKey, setAmbassadorSelectKey] = useState(0);
  const [ambassadorSelectionError, setAmbassadorSelectionError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [initiativeToEdit, setInitiativeToEdit] = useState(null);
  const [initiativeToDelete, setInitiativeToDelete] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchList = async ({
    selectedPage = appliedFilters.selectedPage,
    selectedPageSize = appliedFilters.selectedPageSize,
    ambassadorId = appliedFilters.ambassadorId,
    type = appliedFilters.type,
    status = appliedFilters.status,
    visibility = appliedFilters.visibility,
  } = {}) => {
    const nextFilters = {
      selectedPage,
      selectedPageSize,
      ambassadorId,
      type,
      status,
      visibility,
    };
    setList((prev) => ({ ...prev, loading: true }));
    setAppliedFilters(nextFilters);

    const response: any = await dispatch(getInitiatives(nextFilters) as any);

    if (response?.success) {
      const rawData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      const total =
        typeof response.total === "number"
          ? response.total
          : Array.isArray(rawData)
          ? rawData.length
          : 0;

      setList({
        data: normalizeInitiativesGroupedByAmbassador(rawData),
        total,
        loading: false,
      });
      return;
    }

    setList({
      data: [],
      total: 0,
      loading: false,
    });
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handlePageFilters = ({ selectedPage, selectedPageSize }) => {
    fetchList({ selectedPage, selectedPageSize });
  };

  const handleDraftFilterChange = (field: string, value: string) => {
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    fetchList({
      selectedPage: 1,
      selectedPageSize: appliedFilters.selectedPageSize,
      ambassadorId: draftFilters.ambassadorId,
      type: draftFilters.type,
      status: draftFilters.status,
      visibility: draftFilters.visibility,
    });
  };

  const handleClearAll = () => {
    setSelectedAmbassador(null);
    setAmbassadorList([]);
    setAmbassadorSelectionError("");
    setAmbassadorSelectKey((key) => key + 1);
    setDraftFilters({
      ambassadorId: "",
      type: "",
      status: "",
      visibility: "",
    });
    fetchList({
      selectedPage: 1,
      selectedPageSize: appliedFilters.selectedPageSize,
      ambassadorId: "",
      type: "",
      status: "",
      visibility: "",
    });
  };

  const handleOnAmbassadorChange = async (e) => {
    const text = e?.target?.value;
    setAmbassadorSelectionError("");

    if (typeof text === "object") {
      setSelectedAmbassador(text);
      setDraftFilters((prev) => ({
        ...prev,
        ambassadorId: text?.id || "",
      }));
      return;
    }

    if (!text || text?.length < 3) {
      setAmbassadorList([]);

      if (!text) {
        setSelectedAmbassador(null);
        setDraftFilters((prev) => ({
          ...prev,
          ambassadorId: "",
        }));
      }

      return;
    }

    const res: any = await dispatch(getAmbassadorsListFromSearch(text) as any);

    if (res?.success) {
      const mappedList =
        res?.data?.map((ambassador) => ({
          fullName: convertToFullName(
            ambassador?.firstName,
            ambassador?.lastName
          ),
          id: ambassador?.id,
        })) || [];
      setAmbassadorList(mappedList);
    }
  };

  const handleDownload = async () => {
    const total = list?.total;
    if (!total) return;

    setIsDownloading(true);

    try {
      const res: any = await getInitiativesForDownload({
        total,
        ambassadorId: appliedFilters.ambassadorId,
        type: appliedFilters.type,
        status: appliedFilters.status,
        visibility: appliedFilters.visibility,
      });

      if (res?.success && res?.data?.length) {
        const groups = normalizeInitiativesGroupedByAmbassador(res.data);
        downloadInitiativesListCsv(flattenGroupedInitiatives(groups));
      } else {
        showNotification({
          message: res?.message || "Failed to download initiatives list",
          type: "error",
        });
      }
    } catch {
      showNotification({
        message: "Failed to download initiatives list",
        type: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const openCreateDrawer = () => {
    setInitiativeToEdit(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (initiative) => {
    setInitiativeToEdit(initiative);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setInitiativeToEdit(null);
  };

  const openDeleteConfirm = (initiative) => {
    setInitiativeToDelete(initiative);
  };

  const closeDeleteConfirm = () => {
    setInitiativeToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!initiativeToDelete?.id) return;

    const res: any = await dispatch(
      deleteInitiative(initiativeToDelete.id) as any
    );

    if (res?.success) {
      setInitiativeToDelete(null);
      fetchList();
    }
  };

  const hasActiveFilters = Boolean(
    appliedFilters.ambassadorId ||
      appliedFilters.type ||
      appliedFilters.status ||
      appliedFilters.visibility
  );

  return (
    <Box>
      <Box
        mb={3}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        <ButtonWithIcon
          handleOnClick={openCreateDrawer}
          text={"New Initiative".toUpperCase()}
          icon={{ position: "startIcon", name: "add" }}
        />
      </Box>

      <Grid container spacing={2} mb={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <Typography fontSize={14} fontWeight={400} mb={0.5}>
            Ambassador
          </Typography>
          <AutoSelectbox
            key={ambassadorSelectKey}
            list={ambassadorList}
            listProperties={{
              field: "fullName",
              value: "id",
            }}
            listType={ENUM_AUTOSELECT_LIST_TYPES.ARRAY_OF_OBJECT}
            requireOnChange
            onChange={handleOnAmbassadorChange}
            value={selectedAmbassador}
            label=""
            placeholder="Select Ambassador"
            error={ambassadorSelectionError}
            textfieldStyle={{ height: 7, padding: "7.5px 4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Typography fontSize={14} fontWeight={400} mb={0.5}>
            Type
          </Typography>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={draftFilters.type}
            onChange={(e) => handleDraftFilterChange("type", e.target.value)}
            sx={filterSelectSx}
            renderValue={(value) =>
              value
                ? INITIATIVE_TYPE_OPTIONS.find((option) => option.value === value)
                    ?.label
                : "All"
            }
          >
            <MenuItem value="">All</MenuItem>
            {INITIATIVE_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Typography fontSize={14} fontWeight={400} mb={0.5}>
            Status
          </Typography>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={draftFilters.status}
            onChange={(e) => handleDraftFilterChange("status", e.target.value)}
            sx={filterSelectSx}
            renderValue={(value) =>
              value
                ? STATUS_OPTIONS.find((option) => option.value === value)?.label
                : "All"
            }
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Typography fontSize={14} fontWeight={400} mb={0.5}>
            Visibility
          </Typography>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={draftFilters.visibility}
            onChange={(e) =>
              handleDraftFilterChange("visibility", e.target.value)
            }
            sx={filterSelectSx}
            renderValue={(value) =>
              value
                ? VISIBILITY_OPTIONS.find((option) => option.value === value)
                    ?.label
                : "All"
            }
          >
            {VISIBILITY_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box display="flex" gap={1} alignItems="center">
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={list.loading}
              sx={{
                height: 40,
                px: 2.5,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearAll}
              disabled={list.loading}
              sx={{
                height: 40,
                px: 2.5,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Clear all
            </Button>
          </Box>
        </Grid>
      </Grid>

      {list.loading ? (
        <Box
          py={8}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={36} sx={{ color: "#070639" }} />
        </Box>
      ) : list.data.length === 0 ? (
        <Box py={6} textAlign="center">
          <FormatListBulletedIcon
            sx={{ fontSize: 40, color: "#D1D5DB", mb: 1.5 }}
          />
          <Typography fontSize={15} fontWeight={600} color="#374151">
            No initiatives found
          </Typography>
          <Typography fontSize={13} color="#6B7280" mt={0.5}>
            {hasActiveFilters
              ? "No initiatives match your filters."
              : "Initiatives will appear here once ambassadors add them."}
          </Typography>
        </Box>
      ) : (
        <>
          {Boolean(list?.total) && (
            <Stack direction="row" justifyContent="flex-end" mb={1}>
              <ButtonWithIcon
                handleOnClick={isDownloading ? undefined : handleDownload}
                text={isDownloading ? "DOWNLOADING..." : "DOWNLOAD"}
                icon={{ position: "startIcon", name: "download" }}
                muiIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  opacity: isDownloading ? 0.7 : 1,
                  pointerEvents: isDownloading ? "none" : "auto",
                }}
              />
            </Stack>
          )}
          <InitiativesListTable
            list={list}
            columns={INITIATIVES_GROUP_COLUMNS}
            handlePageFilters={handlePageFilters}
            appliedFilters={appliedFilters}
            onEditClick={openEditDrawer}
            onDeleteClick={openDeleteConfirm}
          />
        </>
      )}

      <AddOrUpdateInitiativeDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        initiative={initiativeToEdit}
        onSuccess={() => fetchList()}
      />

      <AppAlertBox
        isModalOpen={Boolean(initiativeToDelete)}
        onConfirm={handleDeleteConfirm}
        onCancel={closeDeleteConfirm}
        message={DELETE_ALERT_MESSAGE}
      />
    </Box>
  );
}

export default InitiativesList;
